import { isTurnComplete, isAwaitingExternalTool, isAwaitingApproval, isAwaitingToolResult, extractToolUseBlocks, buildToolResults, findLastAssistantMessage, getToolUseIdForResult } from '../utils/chatHelpers';
import { convertFieldValueAvroToJson } from 'rpc-client-utils';
export const RETRY_DELAYS = [500, 1000, 2000];
export function isTransientError(err) {
  if (!err || typeof err !== 'object') return false;
  const asObj = err;
  const status = asObj.status;
  if (typeof status === 'number') {
    return status === 400 || status >= 500;
  }
  // CHIRP errors: the outer error wraps a ChirpError in `cause`.
  // ChirpError has BreezeEngineError fields (including `shouldRetry`) spread
  // directly onto it by spreadPropertiesIntoError in rpc-client-utils.
  // BreezeEngineError has no HTTP status field, so we check errorType directly.
  // INVALID_REQUEST is treated as transient because sendToolResponse returns it
  // when the backend turn hasn't transitioned to AWAITING_TOOL_RESULT yet — a
  // race condition, not a genuinely bad request.
  const cause = asObj.cause;
  if (cause != null && typeof cause === 'object') {
    const causeObj = cause;
    if (causeObj.errorType === 'INVALID_REQUEST') {
      return true;
    }
    const causeStatus = causeObj.status;
    if (typeof causeStatus === 'number') {
      return causeStatus === 400 || causeStatus >= 500;
    }
    if (typeof causeObj.shouldRetry === 'boolean') {
      return causeObj.shouldRetry;
    }
  }
  const message = asObj.message;
  if (typeof message === 'string') {
    return /\b(400|500|502|503|504)\b/.test(message);
  }
  return false;
}
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
export async function withRetry(fn, delays = RETRY_DELAYS) {
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      return await fn();
    } catch (err) {
      const hasRetriesLeft = attempt < delays.length;
      if (hasRetriesLeft && isTransientError(err)) {
        await delay(delays[attempt]);
        continue;
      }
      throw err;
    }
  }
  throw new Error('withRetry: unreachable');
}
function formatRpcError(rpcError) {
  const {
    errorType,
    errorMessage
  } = rpcError;
  if (typeof errorMessage !== 'string') return null;
  if (typeof errorType === 'string') return `${errorType}: ${errorMessage}`;
  return errorMessage;
}

// Local CHIRP tool calls reject with a doubly-wrapped error from rpc-client-utils:
// makeChirpAvroCall re-throws a generic "CHIRP RPC failed for X" error whose
// .cause is a ChirpError, and the raw rpcError payload (with errorType/errorMessage)
// sits at .cause.cause. Unwrap it so the agent sees the real RPC error rather than
// the generic outer message.
function unwrapChirpToolError(err) {
  if (!(err instanceof Error)) return 'Tool execution failed';
  const cause = err.cause;
  if (!cause || typeof cause !== 'object') return err.message;
  const inner = cause.cause;
  if (inner && typeof inner === 'object') {
    const formatted = formatRpcError(inner);
    if (formatted !== null) return formatted;
  }
  const formattedCause = formatRpcError(cause);
  if (formattedCause !== null) return formattedCause;
  return err.message;
}
export function createToolResolver(ctx, getToolMap, registry, retryDelays) {
  const dispatchedToolUseIds = new Set();
  async function doSendToolResponse(toolResults) {
    const state = ctx.getState();
    if (state.conversationId === null || state.turnId === null) return;
    const cId = state.conversationId;
    const tId = state.turnId;
    ctx.dispatch({
      type: 'TOOL_CLEAR'
    });
    ctx.dispatch({
      type: 'STATUS_SET',
      status: ctx.transport.activeStatus
    });
    await withRetry(() => ctx.transport.sendToolResponse(Object.assign({
      conversationId: cId,
      turnId: tId,
      toolResults,
      externalToolSpecs: ctx.getMergedToolSpecs(),
      externalSkillSpecs: ctx.getExternalSkillSpecs(),
      externalSubagentSpecs: ctx.getExternalSubagentSpecs().length > 0 ? ctx.getExternalSubagentSpecs() : undefined
    }, ctx.getResumeMetadata(), ctx.getResumeContexts()), ctx.createHandler()), retryDelays);
  }
  async function checkAndSendToolResponse() {
    const state = ctx.getState();
    if (state.cancelled) return;
    if (state.pendingToolResults.size < state.activeTools.length) return;
    await doSendToolResponse(buildToolResults(state.pendingToolResults, state.activeTools));
  }
  async function executeAutoTool(toolUseId, tool, input) {
    ctx.logger.toolExecutionStart(toolUseId, tool.name, input);
    ctx.telemetry.toolExecutionStarted(toolUseId);
    try {
      const result = await tool.execute(input);
      if (ctx.getState().cancelled) return;
      const toolResult = {
        content: result
      };
      ctx.dispatch({
        type: 'TOOL_RESULT_SET',
        toolUseId,
        result: toolResult,
        status: 'complete'
      });
      ctx.logger.toolExecutionEnd(toolUseId, tool.name, result, 'complete');
      ctx.telemetry.toolExecutionFinished(toolUseId, tool.name, 'complete');
    } catch (err) {
      if (ctx.getState().cancelled) return;
      const errorMessage = unwrapChirpToolError(err);
      const errorResult = {
        content: errorMessage
      };
      ctx.dispatch({
        type: 'TOOL_RESULT_SET',
        toolUseId,
        result: errorResult,
        status: 'error'
      });
      ctx.logger.toolExecutionEnd(toolUseId, tool.name, errorMessage, 'error');
      ctx.telemetry.toolExecutionFinished(toolUseId, tool.name, 'error');
    }
  }
  async function executeRegistryTool(toolUseId, toolName, input) {
    ctx.logger.toolExecutionStart(toolUseId, toolName, input);
    ctx.telemetry.toolExecutionStarted(toolUseId);
    ctx.dispatch({
      type: 'TOOL_STATUS_UPDATED',
      toolUseId,
      update: {
        status: 'executing'
      }
    });
    try {
      const result = await registry.executeTool(toolName, input);
      if (ctx.getState().cancelled) return;
      const resultContent = typeof result === 'string' ? result : JSON.stringify(result);
      ctx.dispatch({
        type: 'TOOL_RESULT_SET',
        toolUseId,
        result: {
          content: resultContent
        },
        status: 'complete'
      });
      ctx.logger.toolExecutionEnd(toolUseId, toolName, resultContent, 'complete');
      ctx.telemetry.toolExecutionFinished(toolUseId, toolName, 'complete');
    } catch (err) {
      if (ctx.getState().cancelled) return;
      const errorMessage = unwrapChirpToolError(err);
      ctx.dispatch({
        type: 'TOOL_RESULT_SET',
        toolUseId,
        result: {
          type: 'TOOL_ERROR',
          message: errorMessage
        },
        status: 'error'
      });
      ctx.logger.toolExecutionEnd(toolUseId, toolName, errorMessage, 'error');
      ctx.telemetry.toolExecutionFinished(toolUseId, toolName, 'error');
    }
  }

  // On persistent-channel transports (Ably) the server continues streaming after
  // tool calls, so don't mark the turn complete. On polling/streaming the channel
  // is dead and completing is the right fallback.
  function completeIfDeadChannel() {
    if (!ctx.transport.isPersistentChannel) {
      ctx.dispatch({
        type: 'TURN_COMPLETED'
      });
    }
  }
  async function handleExternalTool() {
    // Guard against re-entry from polling while a tool is already executing.
    // The polling transport can fire handleExternalTool multiple times before
    // the async tool finishes. Without this guard, each re-entry dispatches
    // TOOL_USE_DETECTED (resetting pendingToolResults and activeTools) and
    // launches another executeRegistryTool, so subsequent TOOL_RESULT_SET
    // dispatches land against stale/empty activeTools — producing toolName: "".
    if (ctx.getState().activeTools.some(t => t.status === 'executing')) {
      return;
    }
    const extracted = extractToolUseBlocks(ctx.getState().messages);
    if (!extracted) {
      completeIfDeadChannel();
      return;
    }
    const {
      toolUseBlocks
    } = extracted;

    // Only handle tools the client knows about. Tools absent from both the
    // toolMap and the registry are executed server-side.
    const clientSideBlocks = toolUseBlocks.filter(block => getToolMap().has(block.name) || registry.hasTool(block.name));
    if (clientSideBlocks.length === 0) {
      completeIfDeadChannel();
      return;
    }

    // Skip any blocks whose toolUseId has already been dispatched. The Ably
    // transport can re-deliver the same stop-reason event (at-least-once
    // delivery) after TOOL_CLEAR resets activeTools, which would otherwise
    // cause the same tool to execute multiple times.
    const newBlocks = clientSideBlocks.filter(block => !dispatchedToolUseIds.has(getToolUseIdForResult(block)));
    if (newBlocks.length === 0) {
      return;
    }
    for (const block of newBlocks) {
      dispatchedToolUseIds.add(getToolUseIdForResult(block));
    }
    ctx.dispatch({
      type: 'TOOL_USE_DETECTED',
      blocks: newBlocks
    });
    const autoRunPromises = [];
    for (const block of newBlocks) {
      const tool = getToolMap().get(block.name);
      const toolUseId = getToolUseIdForResult(block);
      if (!tool) {
        autoRunPromises.push(executeRegistryTool(toolUseId, block.name, convertFieldValueAvroToJson(block.input)));
        continue;
      }
      if (tool.execute) {
        ctx.dispatch({
          type: 'TOOL_STATUS_UPDATED',
          toolUseId,
          update: {
            status: 'executing'
          }
        });
        autoRunPromises.push(executeAutoTool(toolUseId, tool, convertFieldValueAvroToJson(block.input)));
      }
    }
    if (autoRunPromises.length > 0) {
      await Promise.all(autoRunPromises);
    }
    await checkAndSendToolResponse();
  }
  let pendingApprovalDeferred = null;
  async function handleToolApproval() {
    const extracted = extractToolUseBlocks(ctx.getState().messages);
    if (!extracted) {
      ctx.dispatch({
        type: 'TURN_COMPLETED'
      });
      return;
    }
    ctx.dispatch({
      type: 'TOOL_APPROVAL_REQUIRED',
      blocks: extracted.toolUseBlocks
    });
    await new Promise((resolve, reject) => {
      pendingApprovalDeferred = {
        resolve,
        reject
      };
    });
  }
  async function handleStopReason(stopReason) {
    if (isTurnComplete(stopReason)) {
      ctx.dispatch({
        type: 'TURN_COMPLETED'
      });
      return;
    }
    if (isAwaitingExternalTool(stopReason)) {
      await handleExternalTool();
      return;
    }
    if (isAwaitingApproval(stopReason)) {
      await handleToolApproval();
      return;
    }
    if (isAwaitingToolResult(stopReason)) {
      const lastMsg = findLastAssistantMessage(ctx.getState().messages);
      if (!(lastMsg !== null && lastMsg !== void 0 && lastMsg.stopReason)) {
        ctx.dispatch({
          type: 'TURN_ERROR',
          error: {
            type: 'UNKNOWN',
            message: 'Server requires tool result but no stop reason found'
          }
        });
        return;
      }
      if (lastMsg.stopReason === 'TOOLS_REQUIRE_APPROVAL') {
        await handleToolApproval();
      } else {
        await handleExternalTool();
      }
      return;
    }
    ctx.dispatch({
      type: 'TURN_COMPLETED'
    });
  }
  async function resolveToolApproval(toolUseId, approved, denialFeedback) {
    const state = ctx.getState();
    if (state.status !== 'AWAITING_APPROVAL') return;
    if (!state.activeTools.some(tool => tool.toolUseId === toolUseId)) return;
    ctx.dispatch({
      type: 'TOOL_APPROVAL_SET',
      toolUseId,
      approved,
      denialFeedback
    });
    const nextState = ctx.getState();
    if (nextState.pendingApprovals.size < nextState.activeTools.length) return;
    const conversationId = nextState.conversationId;
    const turnId = nextState.turnId;
    if (conversationId === null || turnId === null) return;
    ctx.dispatch({
      type: 'TOOL_CLEAR'
    });
    ctx.dispatch({
      type: 'STATUS_SET',
      status: ctx.transport.activeStatus
    });
    const deferred = pendingApprovalDeferred;
    pendingApprovalDeferred = null;
    try {
      await ctx.transport.sendToolApproval(Object.assign({
        conversationId,
        turnId,
        approvals: [...nextState.pendingApprovals.values()]
      }, ctx.getResumeMetadata(), ctx.getResumeContexts()), ctx.createHandler());
      deferred === null || deferred === void 0 || deferred.resolve();
    } catch (_unused) {
      deferred === null || deferred === void 0 || deferred.resolve();
    }
  }
  function cancelPendingApproval() {
    const deferred = pendingApprovalDeferred;
    pendingApprovalDeferred = null;
    deferred === null || deferred === void 0 || deferred.resolve();
  }
  return {
    handleStopReason,
    handleExternalTool,
    handleToolApproval,
    checkAndSendToolResponse,
    resolveToolApproval,
    cancelPendingApproval
  };
}